/**
 * DOCUMENTATION:
 * Component: Html5SemanticHtml
 * Purpose: Educational page detailing the importance of Semantic HTML for SEO and Accessibility.
 * * * Note for Junior Developers:
 * - We continue to use `useMemo` for static data structures (semanticElements, codingModules)
 * to prevent unnecessary garbage collection and re-allocations during React render cycles.
 * - Notice how the UI consistency is maintained by reusing the exact same styled-components
 * (PageWrapper, SectionCard, etc.). In a larger enterprise app, these styled components
 * should eventually be extracted into a shared `ui-library` folder for maximum DRY compliance.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1.5fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
        padding: "15px",
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    textTransform: "uppercase",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function Html5SemanticHtml() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* UI State */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    /* Scroll Listener for FAB */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* Copy to Clipboard Handler */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ---------------- DATA DEFINITIONS ---------------- */

    // Core Semantic Elements reference table
    const semanticElements = useMemo(() => [
        { tag: "<header>", purpose: "Introductory Content", description: "Contains navigational links, logos, or introductory headings. Can be used multiple times (e.g., inside an article)." },
        { tag: "<nav>", purpose: "Navigation", description: "Wraps the major block of navigation links for the site." },
        { tag: "<main>", purpose: "Primary Content", description: "The dominant content of the document. There should only be one <main> per page." },
        { tag: "<article>", purpose: "Independent Content", description: "Self-contained composition that could be distributed independently (e.g., a blog post, a news story)." },
        { tag: "<section>", purpose: "Thematic Grouping", description: "A thematic grouping of content, typically requiring its own heading (e.g., 'Contact Us', 'Features')." },
        { tag: "<aside>", purpose: "Tangential Content", description: "Content indirectly related to the main content (e.g., sidebars, call-out boxes, related links)." },
        { tag: "<footer>", purpose: "Closing Content", description: "Contains authorship, copyright data, or footer links for its nearest sectioning element." },
    ], []);

    // Coding examples demonstrating semantic vs non-semantic practices
    const codingModules = useMemo(() => [
        {
            title: "1. 'Div Soup' vs. Semantic Architecture",
            code: `\n<div class="header">\n  <div class="nav">Links</div>\n</div>\n<div class="main-content">\n  <div class="post">Content</div>\n</div>\n\n\n<header>\n  <nav>Links</nav>\n</header>\n<main>\n  <article>Content</article>\n</main>`,
            desc: "Using meaningful tags rather than generic <div> elements gives structural meaning to web pages, making them accessible to screen readers and easily indexable by Google."
        },
        {
            title: "2. Structuring an Independent Article",
            code: `<article>\n  <header>\n    <h2>Understanding Flexbox</h2>\n    <p>Published on: <time datetime="2026-10-12">Oct 12, 2026</time></p>\n  </header>\n\n  <section>\n    <h3>What is it?</h3>\n    <p>Flexbox is a 1-dimensional layout model...</p>\n  </section>\n\n  <footer>\n    <p>Author: Jane Developer</p>\n  </footer>\n</article>`,
            desc: "Notice how <header> and <footer> can be reused inside an <article>. The <time> tag is another semantic element that provides machine-readable dates."
        },
        {
            title: "3. Proper Navigation Structure",
            code: `<nav aria-label="Main Navigation">\n  <ul>\n    <li><a href="/">Home</a></li>\n    <li><a href="/about">About Us</a></li>\n    <li><a href="/contact">Contact</a></li>\n  </ul>\n</nav>`,
            desc: "A <nav> element should almost always contain an unordered list (<ul>) of anchor (<a>) tags. The 'aria-label' helps screen readers distinguish between multiple navigation menus on the same page."
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    SEMANTIC HTML5
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* ✅ Theory Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Meaning Beyond Presentation
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            Before HTML5, developers relied heavily on generic <code>&lt;div&gt;</code> and <code>&lt;span&gt;</code> tags to build layouts. While visually effective, these tags carry <strong>zero meaning</strong>. Semantic HTML introduces tags that describe their content to both the browser and the developer. This is the cornerstone of <strong>Web Accessibility (a11y)</strong> for visually impaired users relying on screen readers, and <strong>Search Engine Optimization (SEO)</strong>.
                        </Typography>

                        {/* ✅ Semantic Elements Table */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Core Sectioning Elements</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Tag</div>
                                    <div>Primary Purpose</div>
                                    <div>Usage Context</div>
                                </ResponsiveDataRow>
                            )}
                            {semanticElements.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod", fontFamily: "'Fira Code', monospace" }}>{item.tag}</Box>
                                    <Box sx={{ fontSize: "0.95rem", fontWeight: 500 }}>{item.purpose}</Box>
                                    <Box sx={{ opacity: 0.8, fontSize: "0.85rem", lineHeight: 1.5 }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Coding Modules Section */}
                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            {/* Module Header & Action Buttons */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, lineHeight: 1.6 }}>{item.desc}</Typography>

                            {/* Code Block Presentation */}
                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/html5-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
